import { CollisionFacet, CollisionShapeLocalTransformFacet, ControllerAxisMessage, convertToNumber3, convertToNumber4, ControllerHand, UpdateFacetOnModelMessage, PrimitiveShape } from "flavr-common";
import { Vector3, Quaternion, Matrix4 } from "flavr-math";
import { SELECTION_STRATEGY, SetSelectionStrategyMessage } from "@messages/selection";
import { CreateCollisionShapeMessage } from "@messages/selection";
import { ControllerMovedMessage } from "@messages/controller/ControllerMessages";
import { clockService } from "@modules/xr/ClockService";
import { modelPool, useExternalMessages, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { ensureLocalPlayerControllersModel, getLocalPlayerClientIdxOrThrow, getPlayerControllerModelName } from "@modules/player";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { ControllerView, getControllerMeshInfo } from "./index";
import { AXIS_MAPPING, BUTTON_MAPPING } from "../devices/InputMapping";
import { ButtonState, InputDevice } from "../devices/InputDevice";
export const BUTTON_PRESSED_VALUE_THRESHOLD = 0.225;
const MAX_DISTANCE_FOR_CLICK_SQUARED = 0.1 * 0.1;
const MIN_COS_ANGLE_FOR_CLICK = Math.cos((5 * Math.PI) / 180);
const TEMP_HAND_COLLISION_WORLD_MATRIX = new Matrix4();
const tmpQuaternion = new Quaternion();
const tmpVector34 = new Vector3();
export class ControllerInputDevice extends InputDevice {
    get type() {
        return this.info.type;
    }
    constructor(controllerModule, info, uid, pendingMessages, enabled, hand, baseColor) {
        var _a;
        super(uid, pendingMessages, enabled);
        this.controllerModule = controllerModule;
        this.info = info;
        this.hand = hand;
        this.baseColor = baseColor;
        this.joystickEnabled = true;
        this.worldPosition = new Vector3();
        this.worldOrientation = new Quaternion();
        this.worldScale = new Vector3();
        this.direction = new Vector3();
        this.previousPosition = new Vector3();
        this.buttons = [];
        this.axes = [];
        const buttonMapping = BUTTON_MAPPING[this.type];
        const gamepad = this.info.gamepad;
        for (let i = 0; i < gamepad.buttons.length; ++i) {
            this.buttons.push({
                type: buttonMapping !== undefined ? buttonMapping[`${i}`] : undefined,
                state: ButtonState.RELEASED,
                lastChangeTimeStamp: 0,
                doubleClick: false,
                pressedPosition: new Vector3(),
                pressedDirection: new Vector3()
            });
        }
        if (gamepad.axes) {
            const mapping = AXIS_MAPPING[this.type];
            for (let i = 0; i < gamepad.axes.length; ++i) {
                const axis = mapping ? mapping[i] : undefined;
                this.axes.push({
                    type: axis ? axis.type : undefined,
                    invert: axis ? (_a = axis.invert) !== null && _a !== void 0 ? _a : false : false,
                    value: 0
                });
            }
        }
    }
    createHandCollisionModel(controllerHand) {
        const controllerRootModel = ensureLocalPlayerControllersModel();
        const broadcastProps = useExternalMessages(this._pendingMessages);
        switch (controllerHand) {
            case ControllerHand.LeftHand:
                this.localPlayerHandCollisionModel = modelPool.createModel("localPlayerLeftHandCollisionModel", controllerRootModel, broadcastProps);
                break;
            case ControllerHand.RightHand:
                this.localPlayerHandCollisionModel = modelPool.createModel("localPlayerRightHandCollisionModel", controllerRootModel, broadcastProps);
                break;
            case ControllerHand.Unknown:
                this.localPlayerHandCollisionModel = modelPool.createModel("localPlayerUnknownHandCollisionModel", controllerRootModel, broadcastProps);
                break;
        }
        if (this.localPlayerHandCollisionModel === undefined) {
            console.error("Couldn't create localPlayerHandCollisionModel");
            return;
        }
        const currentRoom = modelPool.getCurrentRoom();
        if (currentRoom !== undefined) {
            this.localPlayerHandCollisionModel.setRoom(currentRoom, broadcastProps);
        }
        const colliderTransform = new Matrix4().makeScale(0.1, 0.1, 0.1);
        const controllerScale = localPlayerContainer.localPlayerRootNode.scale;
        this.localPlayerHandCollisionModel.setFacet(new CollisionFacet(PrimitiveShape.Box, false, false), broadcastProps);
        this.localPlayerHandCollisionModel.setFacet(new CollisionShapeLocalTransformFacet(colliderTransform.toArray()), broadcastProps);
        this.localPlayerHandCollisionModel.setTransformComponents(undefined, undefined, controllerScale, broadcastProps);
    }
    deleteHandCollisionModel() {
        if (this.localPlayerHandCollisionModel !== undefined) {
            modelPool.destroyModel(this.localPlayerHandCollisionModel, Object.assign({ handleChildren: "destroyRecursive", destroyPermanently: true }, useExternalMessages(this._pendingMessages)));
            this.localPlayerHandCollisionModel = undefined;
        }
    }
    getHandCollisionModelUid() {
        var _a;
        return (_a = this.localPlayerHandCollisionModel) === null || _a === void 0 ? void 0 : _a.getUID();
    }
    createModel(showVrController, showButtonInfos, selectionStrategy, hasTrackedHand = false) {
        const mesh = getControllerMeshInfo(this.type, this.hand);
        if (mesh === undefined)
            return;
        const controllerRootModel = ensureLocalPlayerControllersModel();
        this.view = new ControllerView(this.uid, showVrController, mesh, this.hand, this.baseColor, undefined, controllerRootModel.getUID(), getPlayerControllerModelName(getLocalPlayerClientIdxOrThrow(), this.hand), mesh.type, hasTrackedHand);
        this.view.createModel(this._pendingMessages);
        this.view.setShowAnnotations(showButtonInfos, this._pendingMessages);
        const controllerModel = modelPool.getModel(this.uid);
        const currentRoom = modelPool.getCurrentRoom();
        if (controllerModel !== undefined && currentRoom !== undefined) {
            controllerModel.setRoom(currentRoom, useExternalMessages(this._pendingMessages));
        }
        const selectionInfo = mesh.selection;
        if (selectionStrategy === undefined) {
            const { strategy, params } = selectionInfo;
            switch (strategy) {
                case SELECTION_STRATEGY.RAY_SELECT:
                    selectionStrategy = {
                        type: strategy,
                        maxLength: params.max_ray_length,
                        visible: params.ray_visible,
                        color: params.ray_color,
                        headLength: params.ray_head_length,
                        headWidth: params.ray_head_width
                    };
                    break;
                case SELECTION_STRATEGY.PROXIMITY_SELECT:
                    selectionStrategy = {
                        type: strategy,
                        maxLength: params.max_distance
                    };
                    break;
                case SELECTION_STRATEGY.INTEN_SELECT:
                    selectionStrategy = {
                        type: strategy,
                        snappiness: params.snappiness,
                        stickiness: params.stickiness,
                        threshold: params.threshold,
                        coneHeight: params.cone_hight,
                        coneAngle: params.cone_angle
                    };
                    break;
            }
        }
        if (selectionStrategy === undefined)
            return;
        this._pendingMessages.push(new SetSelectionStrategyMessage(this.uid, convertToNumber3(this.info.rayOrigin), convertToNumber4(this.info.rayRotation), selectionStrategy));
    }
    update() {
        this.direction.set(0, 0, -1).applyQuaternion(this.info.orientation);
        this.ensureInputLayout();
        const gamepad = this.info.gamepad;
        for (let i = 0; i < gamepad.buttons.length && i < this.buttons.length; ++i) {
            this.updateButton(i, gamepad.buttons[i], this.buttons[i]);
        }
        if (gamepad.axes && this.joystickEnabled) {
            for (let i = 0; i < gamepad.axes.length && i < this.axes.length; ++i) {
                this.updateAxis(gamepad.axes[i], this.axes[i]);
            }
        }
    }
    ensureInputLayout() {
        var _a;
        const gamepad = this.info.gamepad;
        const buttonMapping = BUTTON_MAPPING[this.type];
        while (this.buttons.length < gamepad.buttons.length) {
            const buttonIndex = this.buttons.length;
            this.buttons.push({
                type: buttonMapping !== undefined ? buttonMapping[`${buttonIndex}`] : undefined,
                state: ButtonState.RELEASED,
                lastChangeTimeStamp: 0,
                doubleClick: false,
                pressedPosition: new Vector3(),
                pressedDirection: new Vector3()
            });
        }
        if (this.buttons.length > gamepad.buttons.length) {
            for (let i = gamepad.buttons.length; i < this.buttons.length; ++i) {
                const button = this.buttons[i];
                if (button.state !== ButtonState.RELEASED) {
                    this.onButtonRelease(button);
                }
            }
            this.buttons.length = gamepad.buttons.length;
        }
        if (gamepad.axes !== undefined) {
            const axisMapping = AXIS_MAPPING[this.type];
            while (this.axes.length < gamepad.axes.length) {
                const axisIndex = this.axes.length;
                const axis = axisMapping ? axisMapping[axisIndex] : undefined;
                this.axes.push({
                    type: axis ? axis.type : undefined,
                    invert: axis ? (_a = axis.invert) !== null && _a !== void 0 ? _a : false : false,
                    value: 0
                });
            }
            if (this.axes.length > gamepad.axes.length) {
                this.axes.length = gamepad.axes.length;
            }
        }
        else if (this.axes.length > 0) {
            this.axes.length = 0;
        }
    }
    updateTransform() {
        var _a, _b, _c;
        const worldPosition = this.worldPosition.copy(this.info.position);
        localPlayerContainer.transformPointFromPlatformToWorldCoordinates(worldPosition);
        const worldOrientation = this.worldOrientation.copy(this.info.orientation);
        localPlayerContainer.transformPlatformToWorldOrientation(worldOrientation);
        const visualPosition = tmpVector34.copy((_a = this.info.visualPosition) !== null && _a !== void 0 ? _a : this.info.position);
        localPlayerContainer.transformPointFromPlatformToWorldCoordinates(visualPosition);
        const visualOrientation = tmpQuaternion.copy((_b = this.info.visualOrientation) !== null && _b !== void 0 ? _b : this.info.orientation);
        localPlayerContainer.transformPlatformToWorldOrientation(visualOrientation);
        const worldScale = localPlayerContainer.getPlatformScale();
        const delta = clockService.getDeltaToLastFrameInSeconds();
        // Calculate linear velocity from position delta if not natively available
        if (!this.info.hasLinearVelocity && delta > 0) {
            // Calculate velocity as (currentPosition - previousPosition) / deltaTime
            this.info.linearVelocity.copy(this.info.position).sub(this.previousPosition).divideScalar(delta);
        }
        const currentRoom = modelPool.getCurrentRoom();
        if (currentRoom !== undefined) {
            const controllerModel = modelPool.getModel(this.uid);
            if (controllerModel !== undefined && controllerModel.getRoom() !== currentRoom) {
                controllerModel.setRoom(currentRoom, useExternalMessages(this._pendingMessages));
            }
            if (this.localPlayerHandCollisionModel !== undefined && this.localPlayerHandCollisionModel.getRoom() !== currentRoom) {
                this.localPlayerHandCollisionModel.setRoom(currentRoom, useExternalMessages(this._pendingMessages));
            }
        }
        this.ensureControllerCollisionShape();
        this._pendingMessages.push(new ControllerMovedMessage(this.uid, convertToNumber3(worldPosition), convertToNumber4(worldOrientation), worldScale, convertToNumber3(this.info.linearVelocity), this.info.hasTrackedHand
            ? {
                position: convertToNumber3(visualPosition),
                orientation: convertToNumber4(visualOrientation)
            }
            : undefined));
        // Collider
        if (this.localPlayerHandCollisionModel !== undefined) {
            TEMP_HAND_COLLISION_WORLD_MATRIX.compose(visualPosition, visualOrientation, this.worldScale.setScalar(worldScale));
            const localTransform = computeLocalMatrixFromWorldMatrix(this.localPlayerHandCollisionModel, TEMP_HAND_COLLISION_WORLD_MATRIX);
            this.localPlayerHandCollisionModel.setTransform(localTransform, useExternalMessages(this._pendingMessages));
        }
        this.previousPosition.copy(this.info.position);
        (_c = this.view) === null || _c === void 0 ? void 0 : _c.update(this._pendingMessages, worldPosition, worldOrientation, this.worldScale.setScalar(worldScale));
    }
    ensureControllerCollisionShape() {
        const controllerModel = modelPool.getModel(this.uid);
        if (controllerModel === undefined)
            return;
        const colliderTransform = new Matrix4().makeScale(0.1, 0.1, 0.1);
        if (controllerModel.getFacet(CollisionFacet) === undefined) {
            this._pendingMessages.push(new UpdateFacetOnModelMessage(this.uid, new CollisionFacet(PrimitiveShape.Box, false, true)));
        }
        this._pendingMessages.push(new CreateCollisionShapeMessage(PrimitiveShape.Box, this.uid, false, true));
        this._pendingMessages.push(new UpdateFacetOnModelMessage(this.uid, new CollisionShapeLocalTransformFacet(colliderTransform.toArray())));
    }
    updateButton(buttonIndex, gamepadButton, button) {
        var _a, _b;
        const isPressed = gamepadButton.value >= 1 - BUTTON_PRESSED_VALUE_THRESHOLD;
        const wasPressed = button.state !== ButtonState.RELEASED;
        if (wasPressed !== isPressed) {
            if (isPressed) {
                this.onButtonPress(button);
                button.pressedPosition.copy(this.info.position);
                button.pressedDirection.copy(this.direction);
                (_a = this.view) === null || _a === void 0 ? void 0 : _a.onButtonPress(this._pendingMessages, buttonIndex);
            }
            else {
                this.onButtonRelease(button);
                (_b = this.view) === null || _b === void 0 ? void 0 : _b.onButtonRelease(this._pendingMessages, buttonIndex);
            }
        }
        else {
            this.updateButtonState(button);
            if (button.state === ButtonState.PRESSED) {
                const distSq = button.pressedPosition.distanceToSquared(this.info.position);
                const cosAngle = button.pressedDirection.dot(this.direction);
                if (distSq > MAX_DISTANCE_FOR_CLICK_SQUARED || cosAngle < MIN_COS_ANGLE_FOR_CLICK) {
                    this.onButtonHold(button);
                }
            }
        }
    }
    updateAxis(currentValue, axes) {
        if (axes.value === currentValue)
            return;
        axes.value = currentValue;
        if (axes.type !== undefined) {
            this._pendingMessages.push(new ControllerAxisMessage(this.uid, axes.type, currentValue * (axes.invert ? -1 : 1)));
        }
    }
    updateButtonMapping(buttonId, type) {
        const buttonIndex = parseInt(buttonId);
        if (isNaN(buttonIndex) && buttonIndex >= 0 && buttonIndex < this.buttons.length) {
            this.onButtonTypeChanged(this.buttons[buttonIndex], type);
        }
    }
    onDisconnect() {
        var _a;
        this.enabled = false;
        for (const button of this.buttons) {
            button.state = ButtonState.RELEASED;
            button.doubleClick = false;
            button.lastChangeTimeStamp = 0;
        }
        (_a = this.view) === null || _a === void 0 ? void 0 : _a.deleteModel(this._pendingMessages);
    }
    forEachButton(callback) {
        for (const button of this.buttons)
            callback(button);
    }
}
